<template>
  <AppointmentDataModal
    v-model="isVisibleModal"
    :title="isFirstStep ? 'Кто будет пациентом?' : 'Данные пациента'"
    :has-close-icon="isFirstStep"
    :has-back-icon="!isFirstStep"
    :content-class="isMobile ? '' : null"
    :persistent="isPersistentDialog"
    :has-divider="!isAnyTelemed"
    :fullscreen="isMobile && !isFirstStep"
    :disabled="isSavingProfile"
    eager
    @appointment-data-modal:back="setFirstStep"
  >
    <template #activator="{ on, attrs }">
      <div class="mb-4">
        <AppointmentDataCard
          ref="patientCard"
          v-bind="selectedPatientCardProps"
          prepend-icon="ui-icon-circle-account"
          qa-locator="appointment_selected_patient_card"
          class="px-2"
          card-class="py-2"
          min-height="56"
          :is-error-state="isErrorState"
          is-flat
        >
          <template
            v-if="isSelectedPatient"
            #icon
          >
            <div
              class="ui-icon-pen cursor-pointer ml-4"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-btn
            v-show="!isSelectedPatient"
            class="mb-2"
            v-bind="attrs"
            color="bg-primary"
            block
            depressed
            v-on="on"
          >
            <span class="primary--text">{{ $t('Выбрать пациента', options) }}</span>
          </v-btn>
        </AppointmentDataCard>
        <v-expand-transition v-if="!isTelemedMedical">
          <div v-if="isUnderagePatient">
            <BannerInfo
              class="text--text mt-1"
              :inner-atom-indent="0"
              :text-outer-atom-indent="2"
            >
              <div class="text-body-1">
                {{ $t('Пациента до 18 лет должен сопровождать взрослый с комплектом документов', options) }}
              </div>
              <div
                class="link-decoration-underline text-body-1 primary--text mt-2"
                @click="isDocumentsInfoModalVisible = true"
              >
                {{ $t('Какие документы нужны', options) }}
              </div>
            </BannerInfo>
            <AppointmentDocumentsInfoModal v-model="isDocumentsInfoModalVisible" />
          </div>
        </v-expand-transition>
        <v-expand-transition>
          <div v-if="isSelectedPatient && !isValidBirthDay">
            <BaseAlert
              data-qa="appointment_patient_card_alert"
              class="mt-2"
              color="text-error"
              :text="invalidBirthDayText"
            />
          </div>
        </v-expand-transition>
      </div>
    </template>
    <div
      ref="modalContent"
      class="h-100 overflow-y-auto"
      :class="{ 'modal-content-desktop': !isMobile }"
    >
      <v-stepper v-model="currentStep">
        <v-stepper-items>
          <v-stepper-content step="1">
            <AppointmentPatientList
              :class="{ 'py-4': !isAnyTelemed }"
              @appointment-patient-list:click-invalid="handleClickInvalidPatient"
              @appointment-patient-list:click-valid="handleClickValidPatient"
            />
          </v-stepper-content>
          <v-stepper-content step="2">
            <AppointmentPatientForm
              class="py-4"
              :class="isMobile ? 'px-2' : 'px-6'"
              :init-patient="initPatient"
              :disabled-fields="disabledPatientFormFields"
              @appointment-patient-form:save="handleSavePatient"
            />
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </div>
  </AppointmentDataModal>
</template>

<script>
import { mapState, mapActions } from 'pinia'

import { isUsedSessionStorage, isJSON } from 'www/utils'
import BannerInfo from 'www/vue_components/common/core/BannerInfo.vue'
import BaseAlert from 'www/vue_components/common/core/BaseAlert.vue'
import AppointmentPageMethods from 'www/vue_components/common/AppointmentPage/mixins/AppointmentPageMethods'
import AppointmentDataCard from 'www/vue_components/common/AppointmentDataCard/AppointmentDataCard.vue'
import AppointmentDataModal from 'www/vue_components/common/AppointmentPage/components/common/AppointmentDataModal.vue'
import AppointmentPatientList from 'www/vue_components/common/FamilyProfiles/components/AppointmentPatientList.vue'
import AppointmentPatientForm from 'www/vue_components/common/FamilyProfiles/components/AppointmentPatientForm.vue'
import AppointmentDocumentsInfoModal from 'www/vue_components/common/AppointmentPage/components/common/AppointmentDocumentsInfoModal.vue'
import { getPatient } from 'www/vue_components/common/AppointmentPage/interfaces'
import { getPartialPatientData, getSpecialityAgesText } from 'www/vue_components/common/AppointmentPage/functions'
import {
  useGlobalDataStore,
  useDoctorDataStore,
  useServiceDataStore,
  useCalendarDataStore,
  useAuthorizationStore, // todo(useAuthMt) - удалить
  useAppointmentDataStore,
  usePreliminaryConsultationStore,
} from 'www/vue_components/common/AppointmentPage/stores'
import useFamilyProfilesStore from 'www/vue_components/common/FamilyProfiles/stores/useFamilyProfilesStore'
import { FAMILY_PROFILES_SESSION_STORAGE_KEY } from 'www/vue_components/common/AppointmentPage/constants'
import useLpuDataStore from 'www/vue_components/common/AppointmentPage/stores/useLpuDataStore'

export default {
  name: 'AppointmentPatientCard',
  components: {
    BaseAlert,
    BannerInfo,
    AppointmentPatientForm,
    AppointmentPatientList,
    AppointmentDataModal,
    AppointmentDataCard,
    AppointmentDocumentsInfoModal,
  },
  mixins: [
    AppointmentPageMethods,
  ],
  data: vm => ({
    isVisibleModalDefault: false,
    isDocumentsInfoModalVisible: false,
    isMobile: window.MOBILE_VERSION,
    disabledPatientFormFields: {
      surname: false,
      name: false,
      patronymic: false,
      birthDay: false,
      gender: false,
    },
    currentStep: 1,
    initPatient: getPatient(),
    resetTimeoutId: null,
    options: { ns: vm.$i18next.language },
  }),
  computed: {
    ...mapState(useGlobalDataStore, [
      'errorData',
    ]),
    ...mapState(useFamilyProfilesStore, [
      'selectedPatient',
      'patientList',
      'isSelectedPatient',
      'patientFormRef',
      'isErrorState',
    ]),
    ...mapState(useFamilyProfilesStore, [
      'birthDayValidationRules',
      'isUnderagePatient',
      'isValidPatient',
      'isValidBirthDay',
      'invalidBirthDayText',
      'isSavingProfile',
    ]),
    ...mapState(useAuthorizationStore, [ // todo(useAuthMt) - удалить
      'isPhoneConfirmed', // todo(useAuthMt) - удалить
    ]),
    ...mapState(useDoctorDataStore, [
      'selectedDoctor',
    ]),
    ...mapState(useAppointmentDataStore, [
      'isAnyTelemed',
      'isShowPhoneOnly',
      'useAuthMt',
    ]),
    ...mapState(useCalendarDataStore, [
      'editableCalendar',
      'isCalendarDateSelected',
    ]),
    ...mapState(usePreliminaryConsultationStore, [
      'useServiceExpressWithoutConsultation',
      'isPreliminaryConsultation',
      'isSelectedDoctorConsultation',
    ]),
    ...mapState(useLpuDataStore, [
      'isTelemedMedical',
    ]),
    selectedPatientCardProps() {
      const { appointmentObjectTypes } = useAppointmentDataStore()

      if (!this.isSelectedPatient) {
        if (appointmentObjectTypes.isServices && this.selectedDoctor?.workplace?.age) {
          return {
            name: this.$i18next.t('Выберите пациента', this.options),
            validationText: `Услуга только для пациентов ${getSpecialityAgesText(this.selectedDoctor.workplace.age, { withBrackets: false })}`,
          }
        }

        return {
          name: this.$i18next.t('Выберите пациента', this.options),
        }
      }

      return {
        name: this.selectedPatient.fio,
        descriptions: this.selectedPatient.age
          ? [{
            text: this.selectedPatient.age,
            qaLocator: 'age',
          }]
          : [],
      }
    },
    isFirstStep() {
      return this.currentStep === 1
    },
    isPersistentDialog() {
      return !this.isSelectedPatient || this.isSavingProfile
    },
    isVisibleModal: {
      get() {
        return ((this.isPhoneConfirmed || this.useAuthMt) && (!this.isSelectedPatient || this.isSavingProfile))
          || this.isVisibleModalDefault
      },
      set(val) {
        this.isVisibleModalDefault = val
      },
    },
  },
  watch: {
    isVisibleModal(isVisible) {
      clearTimeout(this.resetTimeoutId)

      if (!isVisible) {
        this.resetTimeoutId = setTimeout(() => this.setFirstStep(), 300)
      } else {
        this.setFirstStep()
      }
    },
    isFirstStep(isFirst) {
      if (!isFirst) {
        this.patientFormRef.resetValidation()
      }
    },
    isValidPatient(isValid) {
      if (isValid && this.isSelectedPatient) {
        this.updateIsErrorState(false)
      }
    },
    'errorData.isEnabled': {
      handler(isEnabled) {
        if (!isEnabled) {
          this.requestForRelationsAndSlots()
        }
      },
    },
  },
  created() {
    // TODO: Надо поисследовать, как это работает. Сейчас isFamilyRequestDone отсутствует в компоненте
    if (!this.isFamilyRequestDone) {
      if (this.useAuthMt) {
        (async () => {
          await this.requestFamilyProfiles()
          this.loadSavedPatients()
        })()
      } else {
        this.callOrOnceWatch(
          () => this.isPhoneConfirmed,
          async () => {
            await this.requestFamilyProfiles()
            this.loadSavedPatients()
          },
        )
      }
    }
  },
  mounted() {
    this.updateModalContentEl(this.$refs.modalContent)
    this.updatePatientCardEl(this.$refs.patientCard.$el)
  },
  methods: {
    ...mapActions(useFamilyProfilesStore, [
      'updateSelectedPatient',
      'updateIsSelectedPatient',
      'updatePatientCardEl',
      'updatePatientFormRef',
      'updatePatientFormFieldRefs',
      'updatePatientList',
      'updateModalContentEl',
      'updateIsErrorState',
    ]),
    ...mapActions(useFamilyProfilesStore, [
      'requestFamilyProfiles',
      'saveFamilyProfile',
    ]),
    ...mapActions(useServiceDataStore, [
      'requestDoctorEquipmentRelations',
    ]),
    ...mapActions(useCalendarDataStore, [
      'updateIsLoadingSlots',
      'requestForSlots',
    ]),
    ...mapActions(usePreliminaryConsultationStore, [
      'requestDoctorsAndConsultations',
    ]),
    setFirstStep() {
      this.currentStep = 1
    },
    setLastStep() {
      this.currentStep = 2
    },
    handleClickValidPatient(patient) {
      this.isVisibleModal = false
      this.updateSelectedPatient(patient)
      this.updateIsSelectedPatient(true)
      this.requestForRelationsAndSlots()
    },
    async requestForRelationsAndSlots() {
      const { appointmentObjectTypes } = useAppointmentDataStore()

      if (appointmentObjectTypes.isDoctor) {
        return
      }

      if (this.isPreliminaryConsultation && !this.isShowPhoneOnly && this.isCalendarDateSelected) {
        this.updateIsLoadingSlots(true)
        await this.requestDoctorsAndConsultations(this.editableCalendar.selectedDate)

        if (this.isSelectedDoctorConsultation || this.useServiceExpressWithoutConsultation) {
          this.requestForSlots({
            errorRequestCallback: () => {
              if (!this.useServiceExpressWithoutConsultation) {
                this.requestDoctorsAndConsultations(this.lastSelectedDate)
              }
            },
          })
        }
      } else {
        this.requestDoctorEquipmentRelations(this.editableCalendar.selectedDate)
        this.requestForSlots()
      }
    },
    handleClickInvalidPatient({ patient, validationResults }) {
      this.initPatient = { ...patient }
      this.disabledPatientFormFields = validationResults
      this.setLastStep()
    },
    /**
     * @param {object} patient -
     */
    async handleSavePatient(patient) {
      const selectedPatient = {
        ...patient,
        ...getPartialPatientData({
          surname: patient.surname,
          name: patient.name,
          patronymic: patient.patronymic,
          birthDay: patient.birthDay,
        }),
        isOther: false,
      }

      await this.saveFamilyProfile(selectedPatient)

      this.updateSelectedPatient(selectedPatient)

      const existsPatientIndex = this.patientList.findIndex(item => this.isEqualPatients(item, selectedPatient))

      if (existsPatientIndex !== -1) {
        this.updatePatientList([
          ...this.patientList.slice(0, existsPatientIndex),
          selectedPatient,
          ...this.patientList.slice(existsPatientIndex + 1),
        ])
      } else if (patient.isOther) {
        this.patientList.splice(-1, 0, selectedPatient)
      } else {
        const findPatientIndex = this.patientList.findIndex(({ id }) => id === selectedPatient.id)

        if (findPatientIndex !== -1) {
          this.updatePatientList([
            ...this.patientList.slice(0, findPatientIndex),
            selectedPatient,
            ...this.patientList.slice(findPatientIndex + 1),
          ])
        }
      }

      this.updateIsSelectedPatient(true)
      this.savePatient(selectedPatient)
      this.isVisibleModal = false
      this.requestForRelationsAndSlots()
    },
    isEqualPatients(patientOne, patientTwo) {
      return patientOne.birthDay === patientTwo.birthDay
        && patientOne.name.toLowerCase() === patientTwo.name.toLowerCase()
        && patientOne.patronymic.toLowerCase() === patientTwo.patronymic.toLowerCase()
        && patientOne.surname.toLowerCase() === patientTwo.surname.toLowerCase()
        && patientOne.hasPatronymic === patientTwo.hasPatronymic
    },
    loadSavedPatients() {
      if (!isUsedSessionStorage()) {
        return
      }

      const savedPatientsJson = sessionStorage.getItem(FAMILY_PROFILES_SESSION_STORAGE_KEY)

      if (!isJSON(savedPatientsJson)) {
        return
      }

      let savedPatients = JSON.parse(savedPatientsJson)

      if (!Array.isArray(savedPatients)) {
        return
      }

      savedPatients = savedPatients.filter(savedPatient => !this.patientList.some(
        patient => {
          /**
           * Может быть только один главный профиль.
           * Поэтому если с запроса за профилями пришёл основной профиль с какими-то данными, то берём его.
           */
          if (savedPatient.isMain && patient.isMain) {
            return Boolean(
              patient.name
              || patient.surname
              || patient.birthDay
              || patient.patronymic,
            )
          }

          return this.isEqualPatients(patient, savedPatient)
        },
      ))

      const mainPatient = savedPatients.find(({ isMain }) => isMain)
        || this.patientList[0]

      this.updatePatientList([
        mainPatient,
        ...savedPatients.filter(({ isMain }) => !isMain),
        ...this.patientList.slice(1),
      ])

      sessionStorage.setItem(FAMILY_PROFILES_SESSION_STORAGE_KEY, JSON.stringify(savedPatients))
    },
    savePatient(patient) {
      if (!isUsedSessionStorage()) {
        return
      }

      const savedPatientsJson = sessionStorage.getItem(FAMILY_PROFILES_SESSION_STORAGE_KEY)
      let savedPatients = isJSON(savedPatientsJson)
        ? JSON.parse(savedPatientsJson)
        : []

      if (!Array.isArray(savedPatients)) {
        savedPatients = []
      }

      savedPatients = savedPatients.filter(savedPatient => !this.isEqualPatients(patient, savedPatient))
      savedPatients.push(patient)
      sessionStorage.setItem(FAMILY_PROFILES_SESSION_STORAGE_KEY, JSON.stringify(savedPatients))
    },
  },
}
</script>

<style lang="scss" scoped>
.modal-content-desktop {
  min-height: 435px;
}
</style>
